// Marketing Banner Config
// Campaigns auto-rotate based on the current date. To add a new occasion,
// append an entry to `campaigns` below — the one whose [from, until] window
// contains "now" is picked. If none match, the banner is hidden.

type Campaign = {
    /** Campaign starts at this ISO datetime (inclusive) */
    from: string;
    /** Campaign ends at this ISO datetime (inclusive) — also used as the countdown target */
    until: string;
    message: string;
    discount: string;
    discountPercent: number;
    suffix: string;
    ctaText: string;
    ctaHref: string;
    countdownLabel?: string;
};

/** Ordered list of campaigns covering May–August 2026.
 *  Windows must not overlap; first match wins if they do. */
const campaigns: Campaign[] = [
    {
        // Mother's Day — 2026-05-10
        from: '2026-05-07T00:00:00',
        until: '2026-05-11T23:59:59',
        message: "Mother's Day Special — Get",
        discount: '35% OFF',
        discountPercent: 35,
        suffix: 'to celebrate Mom!',
        ctaText: 'Celebrate Now',
        ctaHref: '/lets-talk',
    },
    {
        // Buddha Purnima — 2026-05-31
        from: '2026-05-12T00:00:00',
        until: '2026-05-31T23:59:59',
        message: 'Buddha Purnima Offer — Get',
        discount: '25% OFF',
        discountPercent: 25,
        suffix: 'on every package!',
        ctaText: 'Claim Now',
        ctaHref: '/lets-talk',
    },
    {
        // World Environment Day — 2026-06-05
        from: '2026-06-01T00:00:00',
        until: '2026-06-07T23:59:59',
        message: 'World Environment Day — Get',
        discount: '30% OFF',
        discountPercent: 30,
        suffix: 'and go green with us!',
        ctaText: 'Claim Now',
        ctaHref: '/lets-talk',
    },
    {
        // Father's Day (Jun 21) + International Yoga Day (Jun 21)
        from: '2026-06-08T00:00:00',
        until: '2026-06-21T23:59:59',
        message: "Father's Day & Yoga Day — Get",
        discount: '35% OFF',
        discountPercent: 35,
        suffix: 'on every package!',
        ctaText: 'Celebrate Now',
        ctaHref: '/lets-talk',
    },
    {
        // Mid-summer sale
        from: '2026-06-22T00:00:00',
        until: '2026-07-14T23:59:59',
        message: 'Summer Sale — Get',
        discount: '25% OFF',
        discountPercent: 25,
        suffix: 'on every package!',
        ctaText: 'Claim Now',
        ctaHref: '/lets-talk',
    },
    {
        // Monsoon sale
        from: '2026-07-15T00:00:00',
        until: '2026-08-10T23:59:59',
        message: 'Monsoon Magic — Get',
        discount: '30% OFF',
        discountPercent: 30,
        suffix: 'on every package!',
        ctaText: 'Claim Now',
        ctaHref: '/lets-talk',
    },
    {
        // Independence Day — 2026-08-15
        from: '2026-08-11T00:00:00',
        until: '2026-08-17T23:59:59',
        message: 'Independence Day Offer — Get',
        discount: '40% OFF',
        discountPercent: 40,
        suffix: 'on every package!',
        ctaText: 'Claim Now',
        ctaHref: '/lets-talk',
    },
    {
        // Raksha Bandhan — 2026-08-28
        from: '2026-08-18T00:00:00',
        until: '2026-08-28T23:59:59',
        message: 'Raksha Bandhan Special — Get',
        discount: '30% OFF',
        discountPercent: 30,
        suffix: 'to celebrate the bond!',
        ctaText: 'Celebrate Now',
        ctaHref: '/lets-talk',
    },
    {
        // End-of-summer wrap
        from: '2026-08-29T00:00:00',
        until: '2026-08-31T23:59:59',
        message: 'Last Chance Summer Deal — Get',
        discount: '20% OFF',
        discountPercent: 20,
        suffix: 'before it ends!',
        ctaText: 'Claim Now',
        ctaHref: '/lets-talk',
    },
];

const DEFAULT_COUNTDOWN_LABEL = 'Offer ends in:';

function getActiveCampaign(now: Date = new Date()): Campaign | null {
    const t = now.getTime();
    for (const c of campaigns) {
        const start = new Date(c.from).getTime();
        const end = new Date(c.until).getTime();
        if (t >= start && t <= end) return c;
    }
    return null;
}

const active = getActiveCampaign();

/** Auto-derived from the current date based on `campaigns` above.
 *  Shape kept stable so existing consumers continue to work. */
export const marketingBannerConfig = {
    /** True only when a campaign window covers "now" */
    enabled: active !== null,

    message: active?.message ?? '',
    discount: active?.discount ?? '',
    discountPercent: active?.discountPercent ?? 0,
    suffix: active?.suffix ?? '',
    ctaText: active?.ctaText ?? '',
    ctaHref: active?.ctaHref ?? '/lets-talk',

    /** Countdown target — the active campaign's end date */
    activeUntil: active?.until ?? '1970-01-01T00:00:00',

    countdownLabel: active?.countdownLabel ?? DEFAULT_COUNTDOWN_LABEL,
};

/** Returns true if there is an active campaign and it has not yet expired */
export function isOfferActive(): boolean {
    const { enabled, activeUntil } = marketingBannerConfig;
    return enabled && new Date(activeUntil).getTime() > Date.now();
}

/** Returns the discounted price, rounded to nearest integer */
export function applyDiscount(price: number): number {
    return Math.round(price * (1 - marketingBannerConfig.discountPercent / 100));
}
